import React from "react";
import {
    AppBar,
    Toolbar,
    Box,
    Drawer,
    Divider,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { RiMenu5Fill } from "react-icons/ri";
import { FaTimes, FaUser, FaSignOutAlt, FaSignInAlt, FaUserPlus } from "react-icons/fa";
import { useSelector } from "react-redux";

// Single, unified header — replaces the old two-bar layout (a dark nav bar
// stacked on top of a separate gradient "utility" bar for the ser.adcb.pt
// wordmark and login state). That doubled up on chrome for no real benefit:
// the wordmark was redundant with the logo/home link, and two differently
// colored bars read as dated rather than deliberate. Everything now lives in
// one MUI AppBar/Toolbar, which also gives the mobile nav a real `Drawer`
// (backdrop, focus trap, slide animation) instead of a manually toggled Box
// pushed into normal document flow.
const menuItems = [
    { to: "/", label: "Início" },
    { to: "/about", label: "Sobre Nós" },
    { to: "/team", label: "Equipa" },
    { to: "/games", label: "Jogos" },
    // { to: "/shop", label: "Loja" },
];

const MainAppBar = ({ shrink }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const location = useLocation();

    const uid = useSelector(state => state.user.uid);
    const displayName = useSelector(state => state.user.displayName);
    const people = useSelector(state => state.user.people);
    const currentPersonId = useSelector(state => state.user.currentPersonId);
    const person = Boolean(people) && people.length > 0 ? people.find(p => p.id === currentPersonId) : null;
    const name = Boolean(person) ? (person.shortName ?? person.fullName) : displayName;

    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const closeDrawer = () => setDrawerOpen(false);

    const toolbarMinHeight = shrink || isMobile ? 64 : 86;
    const logoSize = shrink || isMobile ? 40 : 48;
    const logoSrc = `${process.env.PUBLIC_URL}/logo512.png`;

    // Shared by every top-level link (nav items and the auth links) so they
    // all share the exact same font metrics/box model — mixing in a MUI
    // `Button` (different internal padding/line-height) for the auth links
    // was what made "João Carlota" sit visibly off the nav items' baseline.
    const linkSx = (active = false, uppercase = false) => ({
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        color: theme.palette.primary.contrastText,
        textDecoration: "none",
        fontWeight: 700,
        fontSize: "0.95rem",
        letterSpacing: 0.5,
        textTransform: uppercase ? "uppercase" : "none",
        opacity: active ? 1 : 0.85,
        borderBottom: active ? `2px solid ${theme.palette.maize.dark}` : "2px solid transparent",
        paddingBottom: theme.spacing(1),
        transition: "opacity 0.2s, border-color 0.2s",
        "&:hover": { opacity: 1 },
    });

    return (
        <React.Fragment>
            <AppBar
                position="fixed"
                elevation={shrink ? 4 : 0}
                color="primary"
                sx={{ transition: "box-shadow 0.3s" }}
            >
                <Toolbar
                    disableGutters
                    sx={{
                        minHeight: `${toolbarMinHeight}px !important`,
                        transition: "min-height 0.3s",
                        maxWidth: "1400px",
                        width: "100%",
                        mx: "auto",
                        px: { xs: theme.spacing(4), md: theme.spacing(6) },
                        gap: theme.spacing(2),
                    }}
                >
                    <Box
                        component={RouterLink}
                        to="/"
                        sx={{ display: "flex", alignItems: "center", gap: theme.spacing(2), textDecoration: "none" }}
                    >
                        <Box
                            component="img"
                            src={logoSrc}
                            alt="ADCB"
                            sx={{ width: `${logoSize}px`, height: `${logoSize}px`, transition: "width 0.3s, height 0.3s" }}
                        />
                        <Typography
                            color={theme.palette.primary.contrastText}
                            fontWeight="bold"
                            letterSpacing={1}
                            fontSize={isMobile ? "1.1rem" : "1.3rem"}
                            sx={{ display: { xs: "block", sm: "block" } }}
                        >
                            ADCB
                        </Typography>
                    </Box>

                    <Box sx={{ flexGrow: 1 }} />

                    {!isMobile ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: theme.spacing(6) }}>
                            {menuItems.map((item) => (
                                <Typography
                                    key={item.to}
                                    component={RouterLink}
                                    to={item.to}
                                    sx={linkSx(location.pathname === item.to, true)}
                                >
                                    {item.label}
                                </Typography>
                            ))}
                        </Box>
                    ) : null}

                    <Box sx={{ display: "flex", alignItems: "center", gap: theme.spacing(4), ml: !isMobile ? theme.spacing(6) : 0 }}>
                        {!isMobile ? (
                            uid ? (
                                <React.Fragment>
                                    <Typography component={RouterLink} to="/membership" sx={linkSx(location.pathname === "/membership")}>
                                        <FaUser size={13} />
                                        {name}
                                    </Typography>
                                    <Typography component={RouterLink} to="/logout" sx={linkSx()}>
                                        Sair
                                    </Typography>
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    <Typography component={RouterLink} to="/login" sx={linkSx()}>
                                        Entrar
                                    </Typography>
                                    <Typography component={RouterLink} to="/register" sx={linkSx()}>
                                        Registar
                                    </Typography>
                                </React.Fragment>
                            )
                        ) : (
                            <IconButton
                                component={RouterLink}
                                to={uid ? "/membership" : "/login"}
                                sx={{ color: theme.palette.primary.contrastText }}
                                aria-label={uid ? "A minha conta" : "Entrar"}
                            >
                                <FaUser size={18} />
                            </IconButton>
                        )}

                        {isMobile ? (
                            <IconButton
                                edge="end"
                                aria-label="menu"
                                onClick={() => setDrawerOpen(true)}
                                sx={{ color: theme.palette.primary.contrastText }}
                            >
                                <RiMenu5Fill />
                            </IconButton>
                        ) : null}
                    </Box>
                </Toolbar>
            </AppBar>
            {/* Spacer kept in sync with the AppBar's own min-height via the same
                `toolbarMinHeight` value, instead of a separately hardcoded pixel
                figure that used to drift out of sync with the shrink state. */}
            <Toolbar sx={{ minHeight: `${toolbarMinHeight}px !important`, transition: "min-height 0.3s" }} />

            <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer}>
                <Box sx={{ width: 280 }} role="presentation">
                    <Box sx={{ display: "flex", justifyContent: "flex-end", p: theme.spacing(2) }}>
                        <IconButton onClick={closeDrawer} aria-label="fechar menu">
                            <FaTimes />
                        </IconButton>
                    </Box>
                    <List>
                        {menuItems.map((item) => (
                            <ListItemButton
                                key={item.to}
                                component={RouterLink}
                                to={item.to}
                                selected={location.pathname === item.to}
                                onClick={closeDrawer}
                                sx={{ py: theme.spacing(3) }}
                            >
                                <ListItemText
                                    primaryTypographyProps={{ fontWeight: 700, fontSize: "1.05rem" }}
                                    primary={item.label}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                    <Divider />
                    <List>
                        {uid ? (
                            <React.Fragment>
                                <ListItemButton component={RouterLink} to="/membership" onClick={closeDrawer} sx={{ py: theme.spacing(3) }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}><FaUser /></ListItemIcon>
                                    <ListItemText primary={name || "A minha conta"} />
                                </ListItemButton>
                                <ListItemButton component={RouterLink} to="/logout" onClick={closeDrawer} sx={{ py: theme.spacing(3) }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}><FaSignOutAlt /></ListItemIcon>
                                    <ListItemText primary="Sair" />
                                </ListItemButton>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <ListItemButton component={RouterLink} to="/login" onClick={closeDrawer} sx={{ py: theme.spacing(3) }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}><FaSignInAlt /></ListItemIcon>
                                    <ListItemText primary="Entrar" />
                                </ListItemButton>
                                <ListItemButton component={RouterLink} to="/register" onClick={closeDrawer} sx={{ py: theme.spacing(3) }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}><FaUserPlus /></ListItemIcon>
                                    <ListItemText primary="Registar" />
                                </ListItemButton>
                            </React.Fragment>
                        )}
                    </List>
                </Box>
            </Drawer>
        </React.Fragment>
    );
};

export default MainAppBar;
